// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Maryland bills browser API service.
 */
import type { NodeApiClient } from '@/app-shell/services'

/** Maryland bill metadata from the cache. */
export interface SubjectEntry {
  Code: string
  Name: string
}

export interface MarylandBillMetadata {
  BillNumber: string
  CrossfileBillNumber?: string | null
  Synopsis?: string | null
  Title?: string | null
  Status?: string | null
  BroadSubjects?: SubjectEntry[] | null
  NarrowSubjects?: SubjectEntry[] | null
  BillType?: string | null
  BillVersion?: string | null
  has_amendments?: boolean | null
  version?: number
  pdf_hash?: string
}

/** Response from the connector data endpoint */
interface ConnectorDataResponse {
  downloadUrl?: string
  content?: string
  contentType: string
}

/** Combined result from API metadata + S3 download */
export interface MarylandBillsFetchResult {
  bills: MarylandBillMetadata[]
  cacheStatus: string | undefined
}


/**
 * Fetch Maryland bills for a session via connector cache endpoint.
 */
export async function fetchMarylandBills(
  apiClient: NodeApiClient,
  _orgId: string,
  _wsId: string,
  session: string,
): Promise<MarylandBillsFetchResult> {
  const params = new URLSearchParams({ document_type: 'bulk_bills' })
  const url = `/connectors/maryland-bills/caches/${session}/data?${params}`
  const res = await apiClient.get<ConnectorDataResponse>(url)

  if (!res.data.downloadUrl) {
    return { bills: [], cacheStatus: undefined }
  }

  const s3Res = await fetch(res.data.downloadUrl)
  if (!s3Res.ok) {
    throw new Error(`Failed to download bills cache: ${s3Res.status}`)
  }
  const raw = await s3Res.json()
  const bills: MarylandBillMetadata[] = raw.bills ?? []

  return { bills, cacheStatus: 'ready' }
}

/** Formatting entry from formatting.json */
export interface FormattingEntry {
  text: string
  formatting: string[]
  line_number?: number
}

/** Formatting data keyed by page (e.g., "page_1") */
export type FormattingData = Record<string, FormattingEntry[]>

/**
 * Fetch bill formatting data via connector data endpoint.
 */
export async function fetchBillFormatting(
  apiClient: NodeApiClient,
  _orgId: string,
  _wsId: string,
  datasource: string,
  session: string,
  billNumber: string,
  version?: number,
): Promise<FormattingData | null> {
  const connectorId = datasource === 'federal' ? 'federal-bills' : 'maryland-bills'
  const params = new URLSearchParams({ document_type: 'formatting', item_id: billNumber })
  if (version) params.set('version', String(version))
  const url = `/connectors/${connectorId}/caches/${session}/data?${params}`
  const res = await apiClient.get<ConnectorDataResponse>(url)
  if (!res.data.downloadUrl) return null
  const s3Res = await fetch(res.data.downloadUrl)
  if (!s3Res.ok) return null
  return s3Res.json()
}
